import type { ScopeNode } from "@/components/ChannelMenu"
import type { ActualsRow } from "@/pages/Plan/components/ActualsTable"
import { PLAN_NODES, PRODUCT_DATA, SCOPE_TREE, leafChannelsUnder } from "../data"
import { productRows } from "../rollup"

// Mock data for the "where we will land" Plan vs Actual wireframe. Today is
// 25 Oct 2026. Every figure is made up; the full Vietnam year is ~₫879B and a
// scope or category shows its share of it.

export const TODAY_DAY = 25
export const MONTH_DAYS = 31
// Cancels and returns can still change the most recent days.
export const PROVISIONAL_DAYS = 7

const cat = (id: string, name: string, parent_id: string | null, depth: number, is_leaf: boolean): ScopeNode => ({
  id, name, slug: id, parent_id, depth, is_leaf, authorized: true,
})

export const CATEGORY_TREE: ScopeNode[] = [
  cat("all", "All categories", null, 0, false),
  cat("halio", "Halio", "all", 1, false),
  cat("body-care", "Body Care", "halio", 2, true),
  cat("redlight", "Redlight", "halio", 2, true),
  cat("oral-care", "Oral Care", "halio", 2, true),
  cat("okame", "Okame", "all", 1, false),
  cat("skincare", "Skincare", "okame", 2, true),
]

// Plan nodes at or under a category; every node for "all".
export function planNodesUnder(categoryId: string): Set<string> {
  if (categoryId === "all") return new Set(PLAN_NODES.map((n) => n.id))
  const brand = CATEGORY_TREE.find((c) => c.id === categoryId)
  if (brand && !brand.is_leaf) return new Set(CATEGORY_TREE.filter((c) => c.parent_id === categoryId).map((c) => c.id))
  return new Set([categoryId])
}

function rand(seed: number): number {
  const x = Math.sin(seed * 9301 + 49297) * 233280
  return x - Math.floor(x)
}

// Share of the whole Vietnam plan that a scope + category carries.
export function shareOf(scopeId: string, categoryId: string): number {
  const nodes = planNodesUnder(categoryId)
  const ids = new Set(PRODUCT_DATA.filter((p) => nodes.has(p.planNodeId)).map((p) => p.id))
  const all = productRows("vietnam").reduce((s, r) => s + r.plan, 0)
  const part = productRows(scopeId).filter((r) => ids.has(r.id)).reduce((s, r) => s + r.plan, 0)
  return all > 0 ? part / all : 0
}

const YEAR_TARGET = 878_976_883_184
const MONTH_SHAPE = [70, 62, 75, 60, 68, 78, 72, 70, 74, 80, 95, 95]
const ACTUAL_RATIO = [1.02, 0.98, 1.05, 0.92, 0.97, 1.01, 0.95, 0.99, 0.96]
// The forecast for the months still ahead runs a little under plan.
const FORECAST_RATIO = 0.94
const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

// Promo days carry a multiple of a normal day's plan.
const PROMO_WEIGHT: Record<number, number> = { 10: 6, 15: 2.5, 20: 2.5, 25: 3 }
export const PROMO_NAMES: Record<number, string> = { 10: "10.10", 15: "Mid-month", 20: "20.10", 25: "Payday" }

export interface DayPoint {
  day: number
  label: string
  plan: number
  actual: number | null
  provisional: number | null
}

export interface MonthPoint {
  label: string
  plan: number
  actual: number | null
  estimate: number | null
}

export function landing(share: number) {
  const shapeSum = MONTH_SHAPE.reduce((s, v) => s + v, 0)
  const monthPlan = MONTH_SHAPE.map((v) => (v / shapeSum) * YEAR_TARGET * share)

  // October, day by day.
  const weights = Array.from({ length: MONTH_DAYS }, (_, i) => PROMO_WEIGHT[i + 1] ?? 1)
  const weightSum = weights.reduce((s, w) => s + w, 0)
  const days: DayPoint[] = weights.map((w, i) => {
    const day = i + 1
    const plan = (w / weightSum) * monthPlan[9]
    const sold = day <= TODAY_DAY ? plan * (day === 10 ? 0.78 : 0.8 + rand(day) * 0.3) : null
    const provisional = sold != null && day > TODAY_DAY - PROVISIONAL_DAYS
    return { day, label: String(day), plan, actual: provisional ? null : sold, provisional: provisional ? sold : null }
  })
  const octActual = days.reduce((s, d) => s + (d.actual ?? 0) + (d.provisional ?? 0), 0)
  const octPlanToDate = days.filter((d) => d.day <= TODAY_DAY).reduce((s, d) => s + d.plan, 0)
  const octPlanRest = monthPlan[9] - octPlanToDate
  const last7 = days.filter((d) => d.day > TODAY_DAY - PROVISIONAL_DAYS && d.day <= TODAY_DAY)
  const perDayNow = last7.reduce((s, d) => s + (d.provisional ?? 0), 0) / last7.length
  const daysLeft = MONTH_DAYS - TODAY_DAY

  const months: MonthPoint[] = monthPlan.map((plan, m) => {
    if (m < 9) return { label: MONTH_LABELS[m], plan, actual: plan * ACTUAL_RATIO[m], estimate: null }
    if (m === 9) return { label: MONTH_LABELS[m], plan, actual: octActual, estimate: octPlanRest * FORECAST_RATIO }
    return { label: MONTH_LABELS[m], plan, actual: null, estimate: plan * FORECAST_RATIO }
  })
  const yearActual = months.reduce((s, m) => s + (m.actual ?? 0), 0)
  const yearEstimate = yearActual + months.reduce((s, m) => s + (m.estimate ?? 0), 0)

  return {
    year: { target: YEAR_TARGET * share, actual: yearActual, estimate: yearEstimate, months },
    month: {
      plan: monthPlan[9], planToDate: octPlanToDate, actual: octActual, days, daysLeft, perDayNow,
      perDayNeeded: Math.max(monthPlan[9] - octActual, 0) / daysLeft,
      perDayPlanned: octPlanRest / daysLeft,
    },
  }
}

// Table rows, shaped like the real page's props.
export function tableRows(scopeId: string, categoryId: string, kind: "products" | "categories" | "channels"): ActualsRow[] {
  const nodes = planNodesUnder(categoryId)
  const ids = new Set(PRODUCT_DATA.filter((p) => nodes.has(p.planNodeId)).map((p) => p.id))
  const base = (id: string, name: string, actual: number, plan: number, cogs: number): ActualsRow => ({
    id, name, slug: id, actual, plan, planned_actual: actual, unplanned: [], gm: actual > 0 ? (actual - cogs) / actual : null,
  })

  if (kind === "channels") {
    return leafChannelsUnder(scopeId).map((ch) => {
      const figures = PRODUCT_DATA.filter((p) => ids.has(p.id)).flatMap((p) => p.byChannel).filter((c) => c.channel === ch)
      const sum = (k: "actual" | "plan" | "cogs") => figures.reduce((s, c) => s + c[k], 0)
      return base(ch, SCOPE_TREE.find((n) => n.id === ch)?.name ?? ch, sum("actual"), sum("plan"), sum("cogs"))
    })
  }

  const products = productRows(scopeId).filter((r) => ids.has(r.id))
  if (kind === "categories") {
    return PLAN_NODES.filter((n) => nodes.has(n.id)).flatMap((n) => {
      const inNode = products.filter((r) => PRODUCT_DATA.find((p) => p.id === r.id)?.planNodeId === n.id)
      if (inNode.length === 0) return []
      const sum = (k: "actual" | "plan" | "cogs") => inNode.reduce((s, r) => s + r[k], 0)
      return [base(n.id, n.path.join(" › "), sum("actual"), sum("plan"), sum("cogs"))]
    })
  }
  return products.map((r) => ({
    ...base(r.id, r.name.split(" ")[0], r.actual, r.plan, r.cogs),
    slug: undefined, full_name: r.name, plan_node: r.subLabel, units: r.units ?? 0,
  }))
}
